import { useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import * as Popover from "@/components/ui/popover";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ChangeGroupModal } from "@/components/custom/change-group-modal/change-group-modal";
import { useAuthContext } from "@/auth/AuthProvider";
import { useExceptionsStore } from "@/store/exceptions-store";
import { appConfig } from "@/config/app.config";
import { ROUTES } from "@/config/constants";
import type { ProfileMenuItem } from "../Header.types";
import { useProfileMenuItems } from "../hooks/useProfileMenuItems";

export function PersonMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, setActiveGroupId } = useAuthContext();
  const setExceptionId = useExceptionsStore((s) => s.setExceptionId);

  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [changeGroupOpen, setChangeGroupOpen] = useState(false);

  const openChangeGroup = useCallback(() => setChangeGroupOpen(true), []);
  const profileMenuItems = useProfileMenuItems(openChangeGroup);

  const handleProfileMenuItemClick = (path?: string, action?: () => void) => {
    if (action) {
      action();
    } else if (path) {
      navigate({ to: path });
    }
    setProfilePopoverOpen(false);
  };

  const handleChangeGroupSubmit = (groupId: number) => {
    setActiveGroupId(groupId);
    setExceptionId(undefined);
    queryClient.invalidateQueries({ queryKey: ["exceptions"] });
    if (location.pathname !== ROUTES.EXCEPTIONS) {
      navigate({ to: ROUTES.EXCEPTIONS });
    }
    setChangeGroupOpen(false);
  };

  const renderProfileMenuItem = (item: ProfileMenuItem) => (
    <button
      key={item.title}
      type="button"
      onClick={() => handleProfileMenuItemClick(item.path, item.action)}
      className="flex items-center gap-4 p-2 rounded-md hover:bg-secondary-100 text-left"
    >
      {item.icon}
      <div className="flex flex-col">
        <p className="text-[14px] font-medium text-[#000000]">{item.title}</p>
        <p className="text-[14px] font-normal text-[rgba(0,0,0,0.5)]">
          {item.description}
        </p>
      </div>
    </button>
  );

  return (
    <>
      <Popover.Root
        open={profilePopoverOpen}
        onOpenChange={setProfilePopoverOpen}
      >
        <Popover.Trigger asChild>
          <Badge
            className={`bg-[rgba(255,255,255,0.08)] text-[#FFFFFF] h-10 rounded-[40px] flex items-center pt-2 pr-4 pb-2 pl-3 gap-2 font-medium cursor-pointer hover:opacity-50 active:opacity-50 transition-opacity ${
              profilePopoverOpen ? "opacity-50" : ""
            }`}
          >
            <Avatar className="h-6 w-6 flex-shrink-0">
              <AvatarImage alt="Avatar" />
              <span className="material-symbols-outlined text-2xl leading-none text-[#FFFFFF]">
                account_circle
              </span>
            </Avatar>
            <span className="text-[14px] text-[#FFFFFF] truncate leading-[150%]">
              George S
            </span>
          </Badge>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="z-50 w-80 rounded-[16px] border border-secondary-300 bg-white-primary py-2 px-2 shadow-md"
            sideOffset={6}
            align="end"
          >
            <Popover.Arrow className="w-6 h-3 -translate-y-[1px] fill-white-primary" />
            <div className="flex flex-col">
              {profileMenuItems.map((item) => renderProfileMenuItem(item))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {user && (
        <ChangeGroupModal
          open={changeGroupOpen}
          productName={appConfig.name}
          groups={user.groups}
          activeGroupId={user.activeGroupId}
          onCancel={() => setChangeGroupOpen(false)}
          onSubmit={handleChangeGroupSubmit}
        />
      )}
    </>
  );
}
